import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  appThemeApiRef,
  configApiRef,
  storageApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import type { NavContentComponent } from '@backstage/plugin-app-react';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Divider,
  Popover,
  Tooltip,
} from '@material-ui/core';
import PaletteIcon from '@material-ui/icons/PaletteOutlined';
import CheckIcon from '@material-ui/icons/Check';
import ShuffleIcon from '@material-ui/icons/Shuffle';
import BookmarkIcon from '@material-ui/icons/BookmarkBorder';
import { GdlMark } from './GdlLogo';
import { makeStyles } from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import NotificationsIcon from '@material-ui/icons/NotificationsNone';
import SettingsIcon from '@material-ui/icons/Settings';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PublicIcon from '@material-ui/icons/Public';
import TimelineIcon from '@material-ui/icons/Timeline';
import StorageIcon from '@material-ui/icons/Storage';
import HomeIcon from '@material-ui/icons/Home';
import AppsIcon from '@material-ui/icons/Apps';
import DashboardIcon from '@material-ui/icons/Assessment';
import { Capability, canRunAnyWorkflow } from '@internal/datasphere-personas';
import { usePersonaSession } from '@internal/datasphere-personas/react';
import {
  brand,
  DEFAULT_THEME,
  howden,
  HOWDEN_THEMES,
} from '../../themes/howdenTheme';
import {
  AVAILABILITY_LABEL,
  CAPABILITY_AREAS,
  capabilitiesInArea,
  serviceHref,
} from '../capabilities/capabilities';
import { NavDrawer } from './NavDrawer';
import type { NavLink } from './NavDrawer';

export const TOP_NAV_HEIGHT = 64;

// Each theme has its own provider, so changing theme remounts this bar. The
// saved default (or a random pick) must be applied once per page load, or a
// random theme would keep re-rolling itself.
let preferenceApplied = false;

const useStyles = makeStyles(theme => ({
  bar: {
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar,
    // Three columns, so the page links sit in the centre of the bar whatever
    // the width of the logo and the icons either side.
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    gap: theme.spacing(1),
    height: TOP_NAV_HEIGHT,
    padding: theme.spacing(0, 3),
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(0, 1.5), gap: 0 },
    backgroundColor: brand.navBg,
    color: brand.navText,
    boxShadow: '0 1px 0 rgba(0, 0, 0, 0.12)',
  },
  burger: {
    color: brand.navText,
    marginLeft: theme.spacing(-1),
    [theme.breakpoints.up('lg')]: { display: 'none' },
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginRight: theme.spacing(3),
    [theme.breakpoints.down('xs')]: { marginRight: 0, gap: theme.spacing(1) },
    color: brand.navText,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  logo: {
    height: 32,
    width: 32,
    objectFit: 'cover',
    objectPosition: 'left center',
  },
  brandName: { fontWeight: 600, fontSize: 17, letterSpacing: '0.01em' },
  brandSub: {
    fontSize: 13,
    opacity: 0.85,
    paddingLeft: theme.spacing(1.5),
    borderLeft: '1px solid currentColor',
    [theme.breakpoints.down('md')]: { display: 'none' },
  },
  links: {
    display: 'flex',
    alignItems: 'stretch',
    height: TOP_NAV_HEIGHT,
    listStyle: 'none',
    margin: 0,
    padding: 0,
    [theme.breakpoints.down('md')]: { display: 'none' },
  },
  item: { display: 'flex' },
  link: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    height: '100%',
    padding: theme.spacing(0, 1.75),
    color: brand.navText,
    fontSize: 15,
    fontWeight: 600,
    textDecoration: 'none',
    background: 'none',
    border: 0,
    font: 'inherit',
    cursor: 'pointer',
    '&:hover': { backgroundColor: brand.navHover },
  },
  // The nav indicator marks where you are (3:1 or better on the bar).
  linkActive: {
    boxShadow: `inset 0 -3px 0 ${brand.navIndicator}`,
  },
  chevron: { fontSize: 20 },
  spacer: { flexGrow: 1 },
  left: {
    display: 'flex',
    alignItems: 'center',
    justifySelf: 'start',
    minWidth: 0,
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    justifySelf: 'end',
  },
  menuNote: {
    padding: '4px 16px 8px',
    fontSize: 12,
    color: brand.muted,
    maxWidth: 260,
  },
  swatch: {
    display: 'inline-block',
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: `1px solid ${howden.grey10}`,
  },
  action: { color: brand.navText },
  iconLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: '50%',
    color: brand.navText,
    '&:hover': { backgroundColor: brand.navHover },
    // On a phone these are in the burger menu instead.
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  avatar: {
    display: 'flex',
    marginLeft: theme.spacing(1),
    '& .MuiAvatar-root': { width: 34, height: 34 },
  },
  mega: {
    width: 'min(1200px, calc(100vw - 48px))',
    padding: theme.spacing(4),
    borderRadius: 0,
    borderTop: `4px solid ${brand.primary}`,
  },
  megaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
    gap: theme.spacing(4),
  },
  megaTitle: {
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: brand.primary,
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${brand.rule}`,
  },
  megaList: { listStyle: 'none', margin: 0, padding: 0 },
  megaItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    color: brand.ink,
    fontSize: 14,
    lineHeight: 1.35,
    textDecoration: 'none',
    '&:hover': { color: brand.primary, textDecoration: 'underline' },
  },
  megaPlanned: { color: brand.muted },
  megaTag: {
    flexShrink: 0,
    alignSelf: 'flex-start',
    fontSize: 11,
    fontWeight: 600,
    padding: theme.spacing(0, 0.75),
    border: `1px solid ${brand.muted}`,
    color: brand.muted,
  },
  megaFooter: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${brand.border}`,
    fontSize: 14,
    color: brand.muted,
    '& a': {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      color: brand.primary,
      fontWeight: 600,
    },
  },
}));

const SHORT: Record<string, string> = {
  'roadmap-2026': '2026',
  'roadmap-2027': '2027',
};

const isActive = (pathname: string, to: string) =>
  to === '/' ? pathname === '/' : pathname.startsWith(`/${to.split('/')[1]}`);

/**
 * The portal's top navigation bar: logo, the main pages, a Services mega menu
 * listing every service by area, search, notifications and your profile. On
 * narrow screens the page links fold into the burger menu.
 */
// Named component rather than an inline arrow: it calls hooks, and
// react-hooks/rules-of-hooks only recognises hooks inside named components.
// Exported for its test. The name is kept from the old sidebar because the
// nav extension still mounts it.
export const SidebarComponent: NavContentComponent = () => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const persona = usePersonaSession();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const barRef = useRef<HTMLElement>(null);
  const showPlanned =
    useApi(configApiRef).getOptionalBoolean('datasphere.showPlannedServices') ??
    false;

  // Presentation only: the backend refuses these routes for personas that
  // cannot use them (ADR-0010).
  const canInvokeWorkflow = canRunAnyWorkflow(persona.capabilities);
  const canSeeRequests =
    persona.can(Capability.RequestReadOwn) ||
    persona.can(Capability.RequestReadAll);
  const isAdmin = persona.can(Capability.AdminAccess);
  // A Consumer (landing.read only) sees Home and the roadmap, nothing else.
  const canReadCatalog = persona.can(Capability.CatalogRead);

  const themeApi = useApi(appThemeApiRef);
  const [themeMenu, setThemeMenu] = useState<HTMLElement | null>(null);
  const [activeTheme, setActiveTheme] = useState(
    themeApi?.getActiveThemeId?.() ?? DEFAULT_THEME.id,
  );
  // "default" holds a theme id, or "random" for a new theme each visit.
  // Saved through the StorageApi: per browser today, per user if the
  // user-settings backend is added.
  const themeStore = useApi(storageApiRef)?.forBucket?.('datasphere-theme');
  const [preference, setPreference] = useState<string | undefined>(
    themeStore?.snapshot<string>('default')?.value,
  );
  const applyTheme = (id: string) => {
    themeApi?.setActiveThemeId?.(id);
    setActiveTheme(id);
  };
  const savePreference = (value: string | undefined) => {
    setPreference(value);
    if (value === undefined) themeStore?.remove('default');
    else themeStore?.set('default', value);
  };
  useEffect(() => {
    if (preferenceApplied) return;
    preferenceApplied = true;
    if (preference === 'random') {
      const others = HOWDEN_THEMES.filter(t => t.id !== activeTheme);
      applyTheme(others[Math.floor(Math.random() * others.length)].id);
    } else if (preference && HOWDEN_THEMES.some(t => t.id === preference)) {
      applyTheme(preference);
    }
    // Once per visit: later picks in the menu are previews until saved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const activeTitle =
    HOWDEN_THEMES.find(t => t.id === activeTheme)?.title ?? 'this theme';

  const pageLinks: NavLink[] = [
    ...(canReadCatalog ? [{ to: '/', title: 'Home', icon: HomeIcon }] : []),
    ...(canInvokeWorkflow
      ? [{ to: '/create/templates', title: 'Services', icon: AppsIcon }]
      : []),
    ...(canSeeRequests
      ? [{ to: '/spoke-requests', title: 'All Requests', icon: AssignmentIcon }]
      : []),
    ...(canReadCatalog
      ? [
          { to: '/observability', title: 'Observability', icon: DashboardIcon },
          { to: '/spoke-overview', title: 'Hubs & Spokes', icon: PublicIcon },
        ]
      : []),
    { to: '/roadmap', title: 'Roadmap', icon: TimelineIcon },
    ...(isAdmin
      ? [
          {
            to: '/database-admin',
            title: 'Database administration',
            icon: StorageIcon,
          },
        ]
      : []),
  ];
  // The burger menu also lists the icon actions, for small screens.
  const menuLinks: NavLink[] = [
    ...pageLinks,
    { to: '/notifications', title: 'Notifications', icon: NotificationsIcon },
    { to: '/settings', title: 'Settings', icon: SettingsIcon },
  ];
  // Database administration sits with the icons on the right, not in the row.
  const rowLinks = pageLinks.filter(l => l.to !== '/database-admin');

  const serviceAreas = CAPABILITY_AREAS.filter(a => a.id !== 'insights')
    .map(area => ({
      area,
      items: capabilitiesInArea(area.id).filter(
        c => showPlanned || c.availability === 'live',
      ),
    }))
    .filter(({ items }) => items.length > 0);

  return (
    <>
      <header ref={barRef} className={classes.bar}>
        <div className={classes.left}>
          <IconButton
            aria-label="Open menu"
            className={classes.burger}
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </IconButton>
          <RouterLink
            to="/"
            className={classes.brand}
            aria-label="DataSphere home"
          >
            <GdlMark />
            <span className={classes.brandName}>DataSphere</span>
            <span className={classes.brandSub}>Global Data Lake</span>
          </RouterLink>
        </div>
        <nav aria-label="Main">
          <ul className={classes.links}>
            {rowLinks.map(link => (
              <li key={link.to} className={classes.item}>
                <RouterLink
                  to={link.to}
                  className={`${classes.link} ${
                    isActive(pathname, link.to) ? classes.linkActive : ''
                  }`}
                >
                  {link.title}
                </RouterLink>
                {link.to === '/create/templates' ? (
                  <button
                    type="button"
                    className={classes.link}
                    style={{ paddingLeft: 0, paddingRight: 8 }}
                    aria-label="Show all services"
                    aria-haspopup="true"
                    aria-expanded={megaOpen}
                    onClick={() => setMegaOpen(open => !open)}
                  >
                    <ExpandMoreIcon className={classes.chevron} />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </nav>
        <div className={classes.right}>
          <Tooltip title="Colour theme">
            <IconButton
              aria-label="Colour theme"
              aria-haspopup="true"
              className={classes.action}
              onClick={e => setThemeMenu(e.currentTarget)}
            >
              <PaletteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Search pages and services">
            <IconButton
              aria-label="Search pages and services"
              className={classes.action}
              onClick={() => setDrawerOpen(true)}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
          {isAdmin ? (
            <Tooltip title="Database administration">
              <RouterLink
                to="/database-admin"
                aria-label="Database administration"
                className={classes.iconLink}
              >
                <StorageIcon />
              </RouterLink>
            </Tooltip>
          ) : null}
          <Tooltip title="Notifications">
            <RouterLink
              to="/notifications"
              aria-label="Notifications"
              className={classes.iconLink}
            >
              <NotificationsIcon />
            </RouterLink>
          </Tooltip>
          <Tooltip title="Profile and settings">
            <RouterLink
              to="/settings"
              className={classes.avatar}
              aria-label="Profile and settings"
            >
              <UserSettingsSignInAvatar />
            </RouterLink>
          </Tooltip>
        </div>
      </header>

      <Menu
        anchorEl={themeMenu}
        open={!!themeMenu}
        onClose={() => setThemeMenu(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ 'aria-label': 'Colour theme' }}
      >
        {HOWDEN_THEMES.map(spec => (
          <MenuItem
            key={spec.id}
            selected={spec.id === activeTheme}
            onClick={() => applyTheme(spec.id)}
          >
            <ListItemIcon>
              <span
                className={classes.swatch}
                style={{
                  background: `linear-gradient(135deg, ${spec.navBg} 50%, ${spec.accent} 50%)`,
                }}
                aria-hidden
              />
            </ListItemIcon>
            <ListItemText primary={spec.title} />
            {spec.id === activeTheme ? (
              <CheckIcon
                fontSize="small"
                style={{ marginLeft: 16 }}
                aria-label="Selected"
              />
            ) : null}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            savePreference(activeTheme);
            setThemeMenu(null);
          }}
        >
          <ListItemIcon>
            <BookmarkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              preference === activeTheme
                ? `${activeTitle} is your default`
                : `Save ${activeTitle} as my default`
            }
          />
        </MenuItem>
        <MenuItem
          onClick={() =>
            savePreference(preference === 'random' ? undefined : 'random')
          }
        >
          <ListItemIcon>
            <ShuffleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Random theme each visit" />
          {preference === 'random' ? (
            <CheckIcon
              fontSize="small"
              style={{ marginLeft: 16 }}
              aria-label="On"
            />
          ) : null}
        </MenuItem>
        <div className={classes.menuNote}>
          Picking a theme previews it. Your default, or a random theme, is used
          each time you open the portal.
        </div>
      </Menu>

      <Popover
        open={megaOpen}
        onClose={() => setMegaOpen(false)}
        anchorEl={barRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ className: classes.mega }}
        marginThreshold={24}
      >
        <div className={classes.megaGrid}>
          {serviceAreas.map(({ area, items }) => (
            <section key={area.id} aria-label={area.title}>
              <div className={classes.megaTitle}>{area.title}</div>
              <ul className={classes.megaList}>
                {items.map(item => {
                  const planned = item.availability !== 'live';
                  return (
                    <li key={item.id}>
                      <RouterLink
                        to={serviceHref(item)}
                        onClick={() => setMegaOpen(false)}
                        className={`${classes.megaItem} ${
                          planned ? classes.megaPlanned : ''
                        }`}
                      >
                        <span>{item.title}</span>
                        {planned ? (
                          <span
                            className={classes.megaTag}
                            title={AVAILABILITY_LABEL[item.availability]}
                          >
                            {SHORT[item.availability]}
                          </span>
                        ) : null}
                      </RouterLink>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
        <div className={classes.megaFooter}>
          <span>
            Services with a year are on the platform roadmap and open a page
            about what is coming.
          </span>
          <RouterLink to="/create/templates" onClick={() => setMegaOpen(false)}>
            All services <ArrowForwardIcon fontSize="small" aria-hidden />
          </RouterLink>
        </div>
      </Popover>

      <NavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        links={menuLinks}
        showServices={canInvokeWorkflow}
        showPlanned={showPlanned}
        hrefFor={serviceHref}
      />
    </>
  );
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: SidebarComponent,
  },
});
